export const getFormattedUrl = (url: string): string => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  } else {
    return `https://${url}`;
  }
};

export const getSignInUrl = () => '/auth/login/sign-in';

export const getForgotPasswordUrl = () => '/auth/login/forgot-password';

export const getMfaUrl = () => '/auth/login/mfa';

/** BRN-2148: SSO callback landing route (relative). `AuthenticationService.ssoAuthorizeUrl()`
 *  combines this with `window.location.origin` to build the absolute `return_uri` sent to brain-iam. */
export const getSsoCallbackUrl = () => '/auth/login/sso-callback';

/** BRN-2208: the explicit unenriched-session state. Deliberately NOT under `/auth`: `AuthLoginGuard`
 *  bounces anyone holding a valid access token away from `/auth`, and an unenriched session holds a
 *  perfectly valid token -- routing it there would bounce it straight back into the loop this state
 *  exists to end. */
export const getUnenrichedSessionUrl = () => '/session/unenriched';

export const getPortalMapUrl = () => '/portal/map';

/** Post-login entry — home guard routes to onboarding or map. */
export const getPortalHomeUrl = () => '/portal';

export const getDashboardlUrl = () => '/dashboard';

export const getDashboardPortalUrl = (facilityGroupId?: number) => `/portal/dashboard/group/${facilityGroupId}`;

export const getAdvancedAlertingUrl = () => '/portal/alerting/advanced';

export const getBasicAlertingUrl = () => '/portal/alerting/basic';

export const getAllGroupsUrl = () => '/portal/groups/all';

export const getDetailedReportUrl = () => '/portal/report/detailed';
export const getSummaryReportUrl = () => '/portal/report/summary';
export const getChargingStationsReportUrl = () => '/portal/report/charging-stations';
export const getSavingsReportUrl = () => '/portal/report/savings';

/** Group name as one path segment. A name can arrive already encoded (`%20`, `%2520`) and must be
 * normalised to raw text first. `Router.navigate` splits only the first command on `/`, so a name
 * such as `Administration/Offices` has to be its own command — stuffing it into one URL string
 * makes the slash a separator, the facility route misses, and `**` sends the click to the map.
 * `getGroupsUrl` is the string form for `navigateByUrl`, `href`, and `window.open`: the name is
 * encoded once there, because those APIs parse rather than re-encode. */
const rawGroupNameSegment = (groupName?: string): string => {
  if (!groupName) return '';
  let decoded = groupName;
  for (let i = 0; i < 2; i++) {
    if (!decoded.includes('%')) break;
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      break;
    }
  }
  return decoded;
};

export const getGroupsUrlCommands = (
  typeOfReport?: string,
  groupName?: string,
  facilityGroupId?: number,
  facilityId?: number,
): [string, string, string, string, string] => {
  const page = typeOfReport?.replace(/\s+/g, '').toLowerCase();
  const normalizedFacilityId = typeof facilityId === 'number' && Number.isFinite(facilityId) && facilityId > 0 ? facilityId : 0;
  return [`/portal/groups/${page}`, rawGroupNameSegment(groupName), `${facilityGroupId}`, 'facilityId', `${normalizedFacilityId}`];
};

export const getGroupsUrl = (typeOfReport?: string, groupName?: string, facilityGroupId?: number, facilityId?: number) => {
  const [base, groupSegment, groupId, facilityKey, id] = getGroupsUrlCommands(typeOfReport, groupName, facilityGroupId, facilityId);
  return `${base}/${encodeURIComponent(groupSegment)}/${groupId}/${facilityKey}/${id}`;
};
